// Calendar-grid geometry — the weekend column tints + vertical gridlines that
// make up the timeline's static background. Pure + framework-free so both the
// canvas (GridBody) and the sticky-header band can render an identical grid
// without re-deriving the date math twice (and drifting). The sticky band uses
// it to RE-PAINT the calendar behind a pinned header — an opaque row fill hides
// the scrolling bars/arrows, then these stripes/lines read as the empty grid.

import {
  format,
  parseISO,
  getDay,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
} from 'date-fns';
import type { ISODate, SchedulerZoom } from '@interfaces/IScheduler';
import { dateToX, viewMode, PX_PER_DAY } from './timeScale';

/** A shaded non-working column. Sat/Sun keep their JP-convention colours
 *  (blue / red); any other rest weekday uses the neutral `rest` style. */
export interface IRestDayCol {
  x: number;
  width: number;
  kind: 'saturday' | 'sunday' | 'rest';
}

export interface ICalendarGrid {
  /** Rest-day column tints (empty at month zoom, where they'd alias to noise). */
  restDayCols: IRestDayCol[];
  /** X of every gridline tick (day / week / month depending on zoom). */
  tickXs: number[];
  /** Subset of `tickXs` that are week boundaries (major lines) — day zoom only. */
  majorXs: Set<number>;
}

/**
 * A day's rest kind on the site calendar, or null on a working day. The site's
 * rest weekdays decide; with none on record, Saturday and Sunday rest — the
 * weekend default, so a calendar without a work calendar still reads as one.
 * The one rule for every surface that shades rest days (the Gantt's canvas and
 * header, the Activities Work Plan).
 */
export function restDayKind(weekday: number, restDays: ReadonlySet<number> | undefined): IRestDayCol['kind'] | null {
  const rests = restDays !== undefined && restDays.size > 0 ? restDays.has(weekday) : weekday === 0 || weekday === 6;
  if (!rests) return null;
  return weekday === 6 ? 'saturday' : weekday === 0 ? 'sunday' : 'rest';
}

export function buildCalendarGrid(
  origin: ISODate,
  end: ISODate,
  zoom: SchedulerZoom,
  scale: number,
  /** Site rest weekdays (0=Sun … 6=Sat). When non-empty, columns are shaded by
   *  this set; when omitted/empty, falls back to the Sat/Sun weekend default. */
  restDays?: Set<number>,
): ICalendarGrid {
  const start = parseISO(origin);
  const stop = parseISO(end);

  const currentViewMode = viewMode(zoom);
  const tickFn =
    currentViewMode.tick === 'day' ? eachDayOfInterval :
    currentViewMode.tick === 'week' ? (interval: { start: Date; end: Date }) => eachWeekOfInterval(interval, { weekStartsOn: 1 }) :
    eachMonthOfInterval;
  const tickXs = tickFn({ start, end: stop }).map((date) => dateToX(format(date, 'yyyy-MM-dd'), zoom, origin, scale));

  // Major (week-boundary) lines are only distinguished at day zoom; at coarser
  // zooms every tick is already a week/month so there's nothing to emphasise.
  const majorXs = zoom === 'day'
    ? new Set(eachWeekOfInterval({ start, end: stop }, { weekStartsOn: 1 }).map((date) => dateToX(format(date, 'yyyy-MM-dd'), zoom, origin, scale)))
    : new Set<number>();

  // Rest-day tints — visible only at Day / Week zooms where the per-day column is
  // wide enough; at Month zoom (6px/day) the stripes become aliased noise. Which
  // days rest is `restDayKind`'s rule (the site's rest days, else Sat/Sun).
  const restDayCols: IRestDayCol[] = [];
  if (zoom !== 'month') {
    const dayPx = PX_PER_DAY[zoom] * scale;
    for (const date of eachDayOfInterval({ start, end: stop })) {
      const kind = restDayKind(getDay(date), restDays);
      if (!kind) continue;
      restDayCols.push({ x: dateToX(format(date, 'yyyy-MM-dd'), zoom, origin, scale), width: dayPx, kind });
    }
  }

  return { restDayCols, tickXs, majorXs };
}
